// ©2024 Austin App House. All rights reserved.
//
// Elavon / Converge payment API helpers (frontend contract).
//
// Backend endpoints the UI expects:
//
// POST /api/payment/elavon/session
//   vault:    { intent: 'vault' }
//   payment:  { intent: 'payment', amount: number }
//   res:      { sessionId, orderId } | { payload: { sessionId, orderId } }
//
// POST /api/payment/elavon/confirm
//   body: { sessionId, orderId, hostedCardId, amount, saveCard?: boolean, card?: {...} }
//   res:  charge result; when saveCard=true backend also stores hosted card on customer
//
// POST /api/payment/elavon/charge-saved
//   body: { paymentMethodId: string, amount: number, hostedCardId?: string }
//   res:  charge result (pay with vaulted Elavon card, no Hosted Fields UI)
//
// POST /api/payment/elavon/session  (vault — Payment Methods page)
//   body: { intent: 'vault' }
//   res:  { sessionId, orderId }
//
// POST /api/payment/elavon/save-card
//   body: { sessionId, orderId, hostedCardId, card?: {...} }
//   res:  { success, message } — stores hosted card, no charge
//
// GET /api/payment/payment-methods
//   res:  { paymentMethods, savedBankAccounts?, bankPaymentMethods?, elavonPaymentMethods? }
//
// Bank save/charge: see src/utils/paymentBanks.js

import axios from 'axios';
import { mapSavedBanksForSelect, mapSavedBanksForList } from './paymentBanks';

export const ELAVON_SESSION = '/api/payment/elavon/session';
export const ELAVON_CONFIRM = '/api/payment/elavon/confirm';
export const ELAVON_CHARGE_SAVED = '/api/payment/charge-saved-method';
export const ELAVON_SAVE_CARD = '/api/payment/elavon/save-card';

export const PROVIDER_ELAVON = 'elavon';

export function unwrapPayload(data) {
  return data?.payload ?? data;
}

export function isElavonPaymentMethod(pm) {
  if (!pm) return false;
  return (
    pm.provider === PROVIDER_ELAVON
    || pm.elavonHostedCardId
    || pm.hostedCardId
    || pm.type === 'elavon_card'
  );
}

function labelForCard(brand, last4, holderName) {
  const name = brand || 'Card';
  const suffix = last4 ? ` ending with: ${last4}` : '';
  const holder = holderName ? ` (${holderName})` : '';
  return `${name}${suffix}${holder}`;
}

/** Build CustomSelect options from GET /api/payment/payment-methods. */
export function mapPaymentMethodsForSelect(res) {
  const data = res?.data ?? res;

  const banks = mapSavedBanksForSelect(data);

  const elavonList = [
    ...(data.elavonPaymentMethods || []),
    ...(data.paymentMethods || []).filter(isElavonPaymentMethod),
  ];

  const elavonCards = elavonList.map((el) => {
    const card = el.card || el;
    return {
      label: labelForCard(card.brand || el.brand, card.last4 || el.last4 || card.maskedCardNumber ||  el.maskedCardNumber,  card.holderName || el.holderName ),
      value: el.id || el._id,
      type: 'card',
      provider: PROVIDER_ELAVON,
      hostedCardId: el.hostedCardId || el.elavonHostedCardId,
      paymentMethodId: el.id || el._id,
    };
  });

  return [...banks, ...elavonCards];
}

export async function createElavonSession(amount) {
  const { data } = await axios.post(ELAVON_SESSION, {
    intent: 'payment',
    amount: Number(amount),
  });
  const body = unwrapPayload(data);
  return {
    sessionId: body?.sessionId ?? body?.session_id ?? null,
    orderId: body?.orderId ?? body?.order_id ?? null,
    raw: data,
  };
}

/** Vault-only session for Payment Methods (no charge). */
export async function createElavonVaultSession() {
  const { data } = await axios.post(ELAVON_SESSION, { intent: 'vault' });
  const body = unwrapPayload(data);
  return {
    sessionId: body?.sessionId ?? body?.session_id ?? null,
    orderId: body?.orderId ?? body?.order_id ?? null,
    raw: data,
  };
}

export async function saveElavonCard({
  sessionId,
  orderId,
  hostedCardId,
  card,
}) {
  const { data } = await axios.post(ELAVON_SAVE_CARD, {
    sessionId,
    orderId,
    hostedCardId,
    card,
  });
  return data;
}

/** Normalize GET /api/payment/payment-methods for the Payment Methods list UI. */
export function mergePaymentMethodsForList(res) {
  const data = res?.data ?? res;

  const elavonRaw = [
    ...(data.elavonPaymentMethods || []),
    ...(data.paymentMethods || []).filter(isElavonPaymentMethod),
  ];

  const elavonCards = elavonRaw.map((pm) => {
    const card = pm.card || pm;
    return {
      ...pm,
      id: pm.id || pm._id,
      provider: PROVIDER_ELAVON,
      hostedCardId: pm.hostedCardId || pm.elavonHostedCardId,
      card: {
        brand: card.brand || pm.brand,
        last4: card.last4 || pm.last4 || pm.maskedCardNumber,
        exp_month: card.exp_month ?? card.expirationMonth ?? pm.expMonth,
        exp_year: card.exp_year ?? card.expirationYear ?? pm.expYear,
        holderName: card.holderName || pm.holderName,
      },
    };
  });

  const banks = mapSavedBanksForList(data);

  return [...elavonCards, ...banks];
}

export async function confirmElavonPayment({
  sessionId,
  orderId,
  hostedCardId,
  amount,
  saveCard,
  card,
}) {
  const { data } = await axios.post(ELAVON_CONFIRM, {
    sessionId,
    orderId,
    hostedCardId,
    amount: Number(amount),
    saveCard: Boolean(saveCard),
    card,
  });
  return data;
}

export async function chargeSavedElavonCard({
  paymentMethodId,
  hostedCardId,
  amount,
}) {
  const { data } = await axios.post(ELAVON_CHARGE_SAVED, {
    paymentMethodId,
    hostedCardId,
    amount: Number(amount),
  });
  return data;
}
